import { useQuery, keepPreviousData } from '@tanstack/react-query'
import type { Address } from 'viem'
import { getAddress } from 'viem'
import { publicEulerRoutes } from '@/lib/euler-api/publicRouteSpecs'

export interface IndexerPrice {
  address: Address
  price: number
  source: 'pyth' | 'coingecko' | 'pendle' | string
  symbol: string
  timestamp: number
}

export type PricesMap = Map<Address, IndexerPrice>

/**
 * Query key for prices from Euler indexer
 */
export function pricesFromIndexerQueryKey(chainId: number): readonly [string, string, number] {
  return ['euler', 'prices', chainId] as const
}

/**
 * Fetch all asset prices from Euler Indexer API
 * Endpoint: /v1/prices?chainId=999
 */
async function fetchPricesFromIndexer(chainId: number): Promise<PricesMap> {
  const url = publicEulerRoutes.prices.clientUrl(chainId)

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch prices from Euler Indexer: ${response.status} ${response.statusText}`)
  }

  const data: Record<string, IndexerPrice> = await response.json()

  if (!data || typeof data !== 'object') {
    throw new Error('Invalid response format from Euler Indexer prices endpoint')
  }

  // Convert to Map with checksummed addresses
  const pricesMap = new Map<Address, IndexerPrice>()
  for (const [address, priceData] of Object.entries(data)) {
    try {
      const checksummedAddress = getAddress(address)
      pricesMap.set(checksummedAddress, {
        ...priceData,
        address: checksummedAddress,
      })
    } catch (error) {
      console.warn(`Invalid address in prices response: ${address}`, error)
    }
  }

  return pricesMap
}

/**
 * Query options for prices from Euler indexer
 */
export function pricesFromIndexerQueryOptions(chainId: number) {
  return {
    queryKey: pricesFromIndexerQueryKey(chainId),
    queryFn: () => fetchPricesFromIndexer(chainId),
    staleTime: 30_000, // 30 seconds
    gcTime: 60_000, // 1 minute
    refetchInterval: 30_000, // Refetch every 30 seconds for fresh prices
    placeholderData: keepPreviousData, // Prevent flickering during refetches
  }
}

/**
 * Hook to fetch all asset prices from Euler Indexer API
 * Returns a Map of address -> price data
 */
export function usePricesFromIndexer(chainId: number) {
  return useQuery(pricesFromIndexerQueryOptions(chainId))
}

/**
 * Get price for a specific asset from the prices map
 */
export function getPriceFromMap(pricesMap: PricesMap | undefined, assetAddress: Address | undefined): number {
  if (!pricesMap || !assetAddress) return 0

  try {
    const checksummedAddress = getAddress(assetAddress)
    const priceData = pricesMap.get(checksummedAddress)
    return priceData?.price ?? 0
  } catch (error) {
    console.warn(`Failed to get price for asset ${assetAddress}:`, error)
    return 0
  }
}
